import type { AuthenticatedUser } from "@olgen/backend";
import { createAuthClient as createBetterAuthClient } from "better-auth/client";

/**
 * Die Anmeldung, so wie das Frontend sie sieht.
 *
 * BetterAuth liegt bewusst hinter dieser schmalen Schicht, aus drei Gründen:
 *
 * 1. **Dieselbe Falle wie bei Eden.** Ein Client-Generator gibt Typen aus, die
 *    eine Behauptung über die Laufzeit sind, kein Beweis. BetterAuths Sitzung
 *    enthält Felder wie `expiresAt`, bei denen offen ist, ob dort ein String
 *    oder ein `Date` ankommt. Hier gehen ausschließlich Strings hinaus, die
 *    React auch rendern kann. Was nicht durchgereicht wird, kann nicht lügen.
 * 2. **Fehlermeldungen auf Deutsch.** BetterAuth antwortet englisch und mit
 *    Codes. Auf dem Anmeldeformular steht ein Satz, den ein Kunde von beckel
 *    production versteht.
 * 3. **Ein Ort.** `packages/api` ist laut Aufbau die einzige Brücke zum
 *    Backend. Die Anmeldung ist Teil der API, auch wenn sie nicht über Eden
 *    läuft.
 *
 * Ausdrücklich **nicht** benutzt wird `inferAdditionalFields<typeof auth>`:
 * Das zöge den Typ der Server-Instanz und damit `@olgen/db` in den Typecheck
 * des Browsers. Das Frontend darf die Datenbank nicht kennen, auch nicht über
 * einen Typ.
 */

/**
 * Die Konto-Rolle, typgleich mit dem Backend-Vertrag (`AuthenticatedUser`
 * aus `app.ts` — ein reiner Typ-Import, zur Laufzeit ist hier kein
 * Backend-Code). Werte: `mitarbeiter` | `kunde`, siehe Vokabular-Kapitel.
 */
export type KontoRolle = AuthenticatedUser["rolle"];

/**
 * Die Team-Rolle (03.09.2026), ebenfalls typgleich mit dem Backend:
 * `inhaber` | `mitglied` — bei Kundenkonten `null`.
 */
export type TeamRolle = NonNullable<AuthenticatedUser["teamRolle"]>;

/** Was das Frontend von einem angemeldeten Menschen weiß. Nur Strings. */
export type SessionUser = {
	readonly id: string;
	readonly email: string;
	readonly name: string;
	readonly rolle: KontoRolle;
	/** Team-Rolle eines Team-Kontos; `null` bei Kundenkonten. */
	readonly teamRolle: TeamRolle | null;
	/**
	 * Der Speicher-Schlüssel des Profilbilds (BetterAuths `image`-Feld) —
	 * `null` ohne Bild. Das Bild selbst kommt über `/api/konto/avatar/:id`;
	 * der Schlüssel im Link sorgt dafür, dass ein neues Bild sofort sichtbar ist.
	 */
	readonly bild: string | null;
};

export type SignInResult = { readonly ok: true } | { readonly ok: false; readonly message: string };

export type AuthClient = {
	/** `null` heißt: nicht angemeldet. Kein Fehlerfall, kein Wurf. */
	readonly getSession: () => Promise<SessionUser | null>;
	readonly signIn: (args: { email: string; password: string }) => Promise<SignInResult>;
	readonly signOut: () => Promise<void>;
	/**
	 * „Passwort vergessen" (T072): Antwortet IMMER mit ok — ob es die Adresse
	 * gibt, verrät das Formular nicht (sonst wäre es eine Kundenliste).
	 */
	readonly passwortLinkAnfordern: (args: { email: string }) => Promise<SignInResult>;
	/** Setzt das Passwort mit dem Token aus der Mail. */
	readonly passwortSetzen: (args: {
		token: string;
		neuesPasswort: string;
	}) => Promise<SignInResult>;
	/** Profil (angemeldet): den eigenen Anzeigenamen ändern. */
	readonly nameAendern: (args: { name: string }) => Promise<SignInResult>;
	/** Profil (angemeldet): Passwort ändern — das alte muss stimmen, andere Sitzungen enden. */
	readonly passwortAendern: (args: {
		aktuellesPasswort: string;
		neuesPasswort: string;
	}) => Promise<SignInResult>;
};

/**
 * Übersetzt, was BetterAuth an Codes zurückgibt.
 *
 * Absichtlich sagt keine dieser Meldungen, ob es die E-Mail-Adresse gibt.
 * Sonst wäre das Anmeldeformular eine Auskunft darüber, wer Kunde ist.
 */
export function meldungZuFehlercode(args: { code: string | undefined; status: number }): string {
	switch (args.code) {
		case "INVALID_EMAIL_OR_PASSWORD":
		case "INVALID_PASSWORD":
		case "USER_NOT_FOUND":
		case "CREDENTIAL_ACCOUNT_NOT_FOUND":
			return "E-Mail-Adresse oder Passwort stimmt nicht.";
		case "INVALID_EMAIL":
			return "Das sieht nicht nach einer E-Mail-Adresse aus.";
		case "EMAIL_PASSWORD_SIGN_UP_DISABLED":
			return "Konten legt beckel production an. Melde dich bei uns, wenn du noch keines hast.";
		case "KONTO_GESPERRT":
			return "Dieses Konto ist gesperrt. Wende dich an beckel production, wenn das ein Irrtum ist.";
		case "INVALID_ORIGIN":
		case "MISSING_OR_NULL_ORIGIN":
		case "CROSS_SITE_NAVIGATION_LOGIN_BLOCKED":
			// Kein Bedienfehler, sondern ein Fehler in unserer Konfiguration.
			// Deshalb ein Satz, der beim Melden weiterhilft.
			return "Die Anmeldung wurde vom Server abgelehnt (Absender nicht erlaubt). Bitte melde das.";
		default:
			break;
	}

	if (args.status === 429) {
		return "Zu viele Versuche. Bitte warte einen Moment.";
	}

	return `Die Anmeldung hat nicht geklappt (Fehler ${String(args.status)}). Bitte melde das.`;
}

function liesRolle(user: Record<string, unknown>): KontoRolle {
	const wert = user.role;

	return wert === "mitarbeiter" || wert === "kunde" ? wert : "kunde";
}

/** Wie `liesRolle`: unbekannt fällt auf `mitglied`, Kundenkonten haben keine. */
function liesTeamRolle(user: Record<string, unknown>, rolle: KontoRolle): TeamRolle | null {
	if (rolle !== "mitarbeiter") return null;
	return user.teamRolle === "inhaber" ? "inhaber" : "mitglied";
}

export function createAuthClient(args: { baseUrl: string }): AuthClient {
	const client = createBetterAuthClient({
		baseURL: args.baseUrl,
		// Muss zum basePath des Backends passen. Steht dort in
		// `auth.gateway.ts` als AUTH_PREFIX.
		basePath: "/api/auth",
	});

	return {
		getSession: async () => {
			const { data } = await client.getSession();

			if (data === null || data === undefined) {
				return null;
			}

			// Nur diese Felder verlassen die Schicht, und alle sind Strings.
			// Siehe oben, Punkt 1. Die Rolle kommt per Laufzeitprüfung heraus:
			// Ohne `inferAdditionalFields` (bewusst, siehe Kopfkommentar) kennt
			// der Client-Typ das Feld nicht — und ein unbekannter Wert fällt
			// wie im Backend-Gateway auf `kunde`, die kleinste Berechtigung.
			const bild = data.user.image;
			const rolle = liesRolle(data.user);
			return {
				id: String(data.user.id),
				email: String(data.user.email),
				name: String(data.user.name),
				rolle,
				teamRolle: liesTeamRolle(data.user, rolle),
				bild: typeof bild === "string" && bild !== "" ? bild : null,
			};
		},

		signIn: async (anmeldung) => {
			const { error } = await client.signIn.email({
				email: anmeldung.email,
				password: anmeldung.password,
			});

			if (error === null || error === undefined) {
				return { ok: true };
			}

			return {
				ok: false,
				message: meldungZuFehlercode({ code: error.code, status: error.status }),
			};
		},

		signOut: async () => {
			await client.signOut();
		},

		passwortLinkAnfordern: async (args) => {
			// `redirectTo` verlangt BetterAuth, wir bauen den Link aber selbst im
			// Backend-Port — der Wert hier ist nur die Pflichtangabe.
			const { error } = await client.requestPasswordReset({
				email: args.email,
				redirectTo: "/passwort-zuruecksetzen",
			});
			if (error === null || error === undefined || error.status === 404) {
				return { ok: true };
			}
			if (error.status === 429) {
				return { ok: false, message: "Zu viele Versuche. Bitte warte einen Moment." };
			}
			return {
				ok: false,
				message: `Das hat nicht geklappt (Fehler ${String(error.status)}). Bitte melde das.`,
			};
		},

		nameAendern: async (args) => {
			const { error } = await client.updateUser({ name: args.name });
			if (error === null || error === undefined) return { ok: true };
			return {
				ok: false,
				message: `Der Name konnte nicht gespeichert werden (Fehler ${String(error.status)}).`,
			};
		},

		passwortAendern: async (args) => {
			const { error } = await client.changePassword({
				currentPassword: args.aktuellesPasswort,
				newPassword: args.neuesPasswort,
				revokeOtherSessions: true,
			});
			if (error === null || error === undefined) return { ok: true };
			switch (error.code) {
				case "INVALID_PASSWORD":
					return { ok: false, message: "Das aktuelle Passwort stimmt nicht." };
				case "PASSWORD_TOO_SHORT":
					return { ok: false, message: "Das neue Passwort braucht mindestens 12 Zeichen." };
				default:
					return {
						ok: false,
						message: `Das hat nicht geklappt (Fehler ${String(error.status)}). Bitte melde das.`,
					};
			}
		},

		passwortSetzen: async (args) => {
			const { error } = await client.resetPassword({
				newPassword: args.neuesPasswort,
				token: args.token,
			});
			if (error === null || error === undefined) {
				return { ok: true };
			}
			switch (error.code) {
				case "INVALID_TOKEN":
				case "TOKEN_EXPIRED":
					return {
						ok: false,
						message:
							"Dieser Link ist abgelaufen oder wurde schon benutzt. Fordere einfach einen neuen an.",
					};
				case "PASSWORD_TOO_SHORT":
					return { ok: false, message: "Das Passwort braucht mindestens 12 Zeichen." };
				default:
					return {
						ok: false,
						message: `Das hat nicht geklappt (Fehler ${String(error.status)}). Bitte melde das.`,
					};
			}
		},
	};
}
